export type DayOfWeek =
  | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

export const ORDERED_DAYS: DayOfWeek[] = [
  'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'
];

export const DEFAULT_WORKING_DAYS: DayOfWeek[] = [
  'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'
];

/**
 * Week-day order rotated to begin at `startDay` (the plan's start day of week).
 * Used to order the working-days popover list. An unrecognized start day (or
 * Monday) yields the canonical Monday-first order.
 */
export function orderedDaysFrom(startDay: DayOfWeek): DayOfWeek[] {
  const i = ORDERED_DAYS.indexOf(startDay);
  return i <= 0
    ? [...ORDERED_DAYS]
    : [...ORDERED_DAYS.slice(i), ...ORDERED_DAYS.slice(0, i)];
}

/**
 * Locale-provided day letters. `initials` are single letters used when listing days
 * (ambiguity resolved positionally); `rangeAbbr` are the disambiguating abbreviations
 * used at the endpoints of a range. Both are sourced from i18n by the caller.
 */
export interface DayAbbreviations {
  initials: Record<DayOfWeek, string>;
  rangeAbbr: Record<DayOfWeek, string>;
}

function isContiguous(orderedSelection: DayOfWeek[], order: DayOfWeek[]): boolean {
  if (orderedSelection.length <= 1) {
    return true;
  }
  const indices = orderedSelection.map((d) => order.indexOf(d));
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] !== indices[i - 1] + 1) {
      return false;
    }
  }
  return true;
}

/**
 * Days portion of the working-days label (no count). Locale-agnostic — letters come from `abbr`.
 * Days are ordered and contiguity is measured from `startDay` (the plan's start
 * day of week), so the label begins on the first day of the week rather than
 * always Monday.
 * - empty                          -> ""
 * - contiguous run of 5+ days      -> "First-Last"  e.g. "M-F", "Su-Th"
 * - otherwise                      -> initials      e.g. "MTWT", "SMW"
 */
export function formatWorkingDays(
  days: DayOfWeek[], abbr: DayAbbreviations, startDay: DayOfWeek = 'MONDAY'
): string {
  const order = orderedDaysFrom(startDay);
  const selected = order.filter((d) => days.includes(d));
  const count = selected.length;
  if (count === 0) {
    return '';
  }
  if (count >= 5 && isContiguous(selected, order)) {
    return `${abbr.rangeAbbr[selected[0]]}-${abbr.rangeAbbr[selected[count - 1]]}`;
  }
  return selected.map((d) => abbr.initials[d]).join('');
}
